import { useMutation } from '@tanstack/react-query';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { generateK8SToken } from '@/lib/queries/integrations/containers/k8sClusters';

interface TokenGenerationFormProps {
  projectId: string;
}

export function TokenGenerationForm({
  projectId,
}: TokenGenerationFormProps): JSX.Element {
  const [generatedToken, setGeneratedToken] = useState('');
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const { mutateAsync: generateToken, isPending } = useMutation({
    mutationFn: () => generateK8SToken(projectId),
    onSuccess: token => {
      setGeneratedToken(token);
      toast({
        title:
          'integrations.containers.kubernetes.tokenGeneration.tokenGeneratedSuccess',
        description:
          'integrations.containers.kubernetes.tokenGeneration.tokenGeneratedSuccessDescription',
      });
    },
    onError: () => {
      toast({
        title:
          'integrations.containers.kubernetes.tokenGeneration.tokenGenerationError',
        description:
          'integrations.containers.kubernetes.tokenGeneration.tokenGenerationErrorDescription',
        variant: 'destructive',
      });
    },
  });

  const handleGenerateToken = async () => {
    await generateToken();
  };

  const copyTokenToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedToken);
      setCopied(true);
      toast({
        title:
          'integrations.containers.kubernetes.tokenGeneration.tokenCopiedSuccess',
        description:
          'integrations.containers.kubernetes.tokenGeneration.tokenCopiedSuccessDescription',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title:
          'integrations.containers.kubernetes.tokenGeneration.tokenCopyError',
        description:
          'integrations.containers.kubernetes.tokenGeneration.tokenCopyErrorDescription',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="mt-8">
      <h3 className="text-2xl mb-4">
        <FormattedMessage id="integrations.containers.kubernetes.tokenGeneration.title" />
      </h3>
      <p className="mb-6">
        <FormattedMessage id="integrations.containers.kubernetes.tokenGeneration.description" />
      </p>
      <Button
        onClick={handleGenerateToken}
        disabled={isPending}
        className="bg-blue-600 hover:bg-blue-700 text-white mb-6"
      >
        <FormattedMessage
          id={
            isPending
              ? 'integrations.containers.kubernetes.tokenGeneration.generatingButton'
              : 'integrations.containers.kubernetes.tokenGeneration.generateButton'
          }
        />
      </Button>
      {generatedToken !== '' && (
        <div className="mb-8">
          <Label className="font-medium">
            <FormattedMessage id="integrations.containers.kubernetes.tokenGeneration.generatedTokenLabel" />
          </Label>
          <div className="mt-2 p-4 bg-gray-50 dark:bg-gray-800 border dark:border-gray-600 rounded-lg font-mono text-sm relative">
            <div className="pr-12 break-all text-gray-900 dark:text-gray-100">
              {generatedToken}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2"
              onClick={copyTokenToClipboard}
            >
              {copied ? (
                <CheckIcon className="h-4 w-4" />
              ) : (
                <CopyIcon className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
            <FormattedMessage id="integrations.containers.kubernetes.tokenGeneration.tokenHelper" />
          </p>
        </div>
      )}
    </div>
  );
}
